import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, FileSignature, Download, AlertTriangle, CheckCircle2, Info, LogIn,
} from 'lucide-react';
import { SignVariationDialog } from '../components/SignVariationDialog';

const API = '/api';

// Same brand logo used on the contract-pack signing page and portal login.
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_electrical-tracker-1/artifacts/8ty3m189_Updated%20Logo%20White.png';

/**
 * Public signing page for Variations of Contract AND Letters of Authority
 * (both ride the same `variation_signing` token flow on the backend). No
 * login required — auth is by URL token.
 *
 * Brand surface mirrors PublicSignContract.jsx so customers get a
 * consistent Helios experience across contract / variation / LoA signing:
 *   - Dark blue gradient background
 *   - Logo lock-up header
 *   - Two-step card layout (Review / Sign)
 *   - Blue gradient CTAs
 *   - Customer portal login link surfaced on the success state
 *
 * Doc-type-aware labels: the same page handles both `variation_of_contract`
 * and `letter_of_authority` documents — we relabel the page title,
 * subtitle, step copy and confirmation strings based on `doc_tag` so
 * customers always see the right document type.
 */
export const PublicSignVariation = () => {
  const { token } = useParams();
  const [variation, setVariation] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [signOpen, setSignOpen] = useState(false);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/public/variations/${token}`);
      setVariation(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Link is invalid or has expired');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submitFn = async (signature_data_url, signed_name) => {
    const r = await axios.post(`${API}/public/variations/${token}/sign`, {
      signature_data_url,
      signed_name,
    });
    return r.data;
  };

  const fileUrl = `${API}/public/variations/${token}/file`;
  const isLoa = variation?.doc_tag === 'letter_of_authority';
  // UK spelling — Helios is a UK installer and the LoA cover/body PDF uses
  // "Authority" (the legal term) but the customer-facing signing page now
  // uses the natural UK English spelling "Authorisation".
  const docLabel = isLoa ? 'Letter of Authorisation' : 'Contract Variation';
  const downloadFilename = isLoa ? 'HeliosLetterOfAuthorisation.pdf' : 'HeliosContractVariation.pdf';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-sky-800">
        <Loader2 className="w-8 h-8 animate-spin text-white/80" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-sky-800 p-6" data-testid="sign-public-error">
        <Card className="max-w-md w-full border-rose-200 shadow-2xl">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-900 mb-1">Cannot open this link</h1>
            <p className="text-sm text-slate-500">{error}</p>
            <p className="text-xs text-slate-400 mt-4">
              If you believe this is an error, please contact us at{' '}
              <a className="underline text-blue-600" href="mailto:hello@helioses.co.uk">hello@helioses.co.uk</a>.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (variation?.signed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-sky-800 p-6 flex items-center justify-center" data-testid="sign-variation-success">
        <div className="max-w-md w-full">
          <div className="flex items-center justify-center mb-6">
            <img
              src={LOGO_URL}
              alt="Helios Electrical and Solar"
              className="h-12 w-auto drop-shadow-md"
              data-testid="sign-variation-success-logo"
            />
          </div>
          <Card className="border-emerald-200 shadow-2xl">
            <CardContent className="p-8 text-center space-y-4">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Thank you!</h1>
              <p className="text-sm text-slate-600 leading-relaxed">
                {isLoa
                  ? 'Your Letter of Authorisation has been signed successfully. We can now act on your behalf in the relevant DNO application.'
                  : 'Your Contract Variation has been signed successfully. Helios Electrical & Solar will now counter-sign and you\'ll receive a fully-executed copy by email shortly.'}
              </p>
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{docLabel} signed</Badge>
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500 mb-2">Track your installation, view documents, and chat with us:</p>
                <Button
                  asChild
                  className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white"
                  data-testid="variation-success-portal-login-btn"
                >
                  <Link to="/portal/login">
                    <LogIn className="w-4 h-4 mr-2" /> Log in to your customer portal
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-sky-800 py-8 px-4" data-testid="public-sign-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header — logo lock-up matches PublicSignContract */}
        <div className="text-center">
          <div className="flex items-center justify-center">
            <img
              src={LOGO_URL}
              alt="Helios Electrical and Solar"
              className="h-14 w-auto drop-shadow-md"
              data-testid="sign-variation-logo"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mt-4 font-['Manrope'] tracking-tight">
            {isLoa ? 'Your Letter of Authorisation is Ready to Sign' : 'Your Contract Variation is Ready to Sign'}
          </h1>
          <p className="text-sm text-sky-100 mt-1">
            Hi <strong className="text-white">{variation?.customer?.first_name}</strong>, please review the document below and sign at your convenience.
          </p>
          {isLoa && (
            <div className="mt-3 max-w-2xl mx-auto px-3" data-testid="loa-page-description">
              <p className="text-sm text-sky-50/95 leading-relaxed bg-white/5 border border-white/10 rounded-md py-2 px-3 backdrop-blur-sm">
                This Letter of Authorisation is sometimes required for us to act on your behalf with the
                <strong className="text-white"> Distribution Network Operator (DNO)</strong> when applying for your grid connection — for example, when notifying them of a new solar PV or battery system. Signing here gives Helios written permission to lodge the application and respond to the DNO's queries on your behalf.
              </p>
            </div>
          )}
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="mt-3 text-sky-100 hover:text-white hover:bg-white/10 border border-white/20"
            data-testid="sign-variation-portal-login-btn"
          >
            <Link to="/portal/login">
              <LogIn className="w-3.5 h-3.5 mr-1.5" /> Customer portal login
            </Link>
          </Button>
        </div>

        {/* PDF — wrapped in a white card on the dark backdrop */}
        <Card className="shadow-2xl border-0">
          <CardHeader className="pb-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-sky-50">
            <CardTitle className="text-base flex items-center gap-2 text-blue-900">
              <FileSignature className="w-5 h-5 text-blue-600" />
              Step 1 — Review the {docLabel}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            <iframe
              src={fileUrl}
              className="w-full h-[60vh] border-2 border-blue-100 rounded-md bg-slate-50"
              title={docLabel}
              data-testid="public-variation-iframe"
            />
            <div
              className="flex items-start gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-md p-2.5"
              data-testid="iframe-loading-hint"
            >
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
              <p className="leading-relaxed">
                If the preview above is taking a while to load, please refresh your browser or use the <strong>Download</strong> button to open it locally — large documents can take a moment on slower connections.
              </p>
            </div>

            {variation?.variation_summary && !isLoa && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                {variation.variation_summary.original_contract_sum !== undefined && (
                  <div className="p-2 rounded border border-slate-200 bg-slate-50">
                    <p className="text-[11px] uppercase text-slate-500">Original total</p>
                    <p className="font-semibold">£{Number(variation.variation_summary.original_contract_sum || 0).toFixed(2)}</p>
                  </div>
                )}
                {variation.variation_summary.value_of_instruction !== undefined && (
                  <div className="p-2 rounded border border-slate-200 bg-slate-50">
                    <p className="text-[11px] uppercase text-slate-500">Net change</p>
                    <p className="font-semibold">£{Number(variation.variation_summary.value_of_instruction || 0).toFixed(2)}</p>
                  </div>
                )}
                {variation.variation_summary.adjusted_total !== undefined && (
                  <div className="p-2 rounded border border-blue-200 bg-blue-50">
                    <p className="text-[11px] uppercase text-blue-700">New total</p>
                    <p className="font-semibold text-blue-700">£{Number(variation.variation_summary.adjusted_total || 0).toFixed(2)}</p>
                  </div>
                )}
              </div>
            )}

            <Button
              variant="outline"
              asChild
              className="w-full sm:w-auto border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="public-variation-download-btn"
            >
              <a href={fileUrl} target="_blank" rel="noreferrer" download={downloadFilename}>
                <Download className="w-4 h-4 mr-2" /> Download a copy for your records
              </a>
            </Button>
          </CardContent>
        </Card>

        {/* Sign CTA */}
        <Card className="shadow-2xl border-0">
          <CardHeader className="pb-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-sky-50">
            <CardTitle className="text-base flex items-center gap-2 text-blue-900">
              <FileSignature className="w-5 h-5 text-blue-600" />
              Step 2 — Sign &amp; Accept
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            <p className="text-sm text-slate-700 leading-relaxed">
              {isLoa
                ? 'By signing, you confirm that you are the legal owner of the property and authorise Helios Electrical & Solar to act on your behalf in the relevant DNO application.'
                : 'By signing, you confirm that you have read the changes set out in this Variation of Contract and that you authorise Helios Electrical & Solar to proceed on the revised terms.'}
            </p>
            <Button
              onClick={() => setSignOpen(true)}
              className="w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-md"
              data-testid="public-sign-btn"
            >
              <FileSignature className="w-5 h-5 mr-2" /> Sign Now
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-[11px] text-sky-200/70 pt-2">
          Helios Electrical &amp; Solar · MCS-accredited installer · helioses.co.uk ·{' '}
          <Link to="/privacy-policy" className="underline hover:text-white" data-testid="privacy-policy-link">
            Privacy Policy
          </Link>
        </p>
      </div>

      <SignVariationDialog
        open={signOpen}
        onOpenChange={setSignOpen}
        variation={variation}
        defaultName={`${variation?.customer?.first_name || ''} ${variation?.customer?.last_name || ''}`.trim()}
        submitFn={submitFn}
        onSigned={() => { setSignOpen(false); load(); }}
      />
    </div>
  );
};

export default PublicSignVariation;
